/**
 * Authentication API functions
 */

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  user: UserInfo;
  token: string;
}

export interface UserInfo {
  id: number;
  email: string;
  full_name: string;
  profile_type: 'diretor' | 'vendedor';
}

export interface VerifyTokenResponse {
  success: boolean;
  user?: UserInfo;
  error?: string;
}

export interface AuthApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// API base URL for the production backend (includes /api prefix)
const API_BASE = 'https://dexcobackend-v1-production.up.railway.app/api';

// Token storage keys
const TOKEN_KEY = 'dexco_token';
const USER_INFO_KEY = 'dexco_user_info';

/**
 * Token Management Functions
 */
export const tokenStorage = {
  setToken: (token: string) => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  setUserInfo: (userInfo: UserInfo) => {
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo));
  },

  getUserInfo: (): UserInfo | null => {
    const userInfo = localStorage.getItem(USER_INFO_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  },

  clearAll: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_INFO_KEY);
  }
};

/**
 * Login with email and password
 */
export const login = async (credentials: LoginCredentials): Promise<AuthApiResponse<{ user: UserInfo; token: string }>> => {
  try {
    console.log('📤 Attempting login for user:', credentials.email);
    
    const response = await fetch(`${API_BASE}/auth/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(credentials)
    });

    if (!response.ok) {
      if (response.status === 401) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Credenciais inválidas');
      } else if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Dados inválidos');
      } else if (response.status === 429) {
        throw new Error('Muitas tentativas de login. Tente novamente em alguns minutos.');
      } else {
        throw new Error(`Erro de conexão: ${response.status}`);
      }
    }

    const data: LoginResponse = await response.json();
    
    if (data.success && data.user && data.token) {
      // Store token and user info
      tokenStorage.setToken(data.token);
      tokenStorage.setUserInfo(data.user);
      
      console.log('✅ Login successful for user:', data.user.profile_type);
      
      return {
        success: true,
        data: {
          user: data.user,
          token: data.token
        }
      };
    } else {
      throw new Error('Resposta inválida do servidor');
    }
  } catch (error) {
    console.error('❌ Login error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao fazer login'
    };
  }
};

/**
 * Verify JWT token and get user information
 */
export const verifyToken = async (): Promise<VerifyTokenResponse> => {
  try {
    const token = tokenStorage.getToken();
    
    if (!token) {
      return {
        success: false,
        error: 'Token não encontrado'
      };
    }

    const response = await fetch(`${API_BASE}/auth/verify/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token invalid or expired
        tokenStorage.clearAll();
        return {
          success: false,
          error: 'Token inválido ou expirado'
        };
      } else {
        throw new Error(`Erro de verificação: ${response.status}`);
      }
    }

    const data = await response.json();
    
    if (data.success && data.user) {
      console.log('✅ Token verified for user:', data.user.profile_type);
      return {
        success: true,
        user: data.user
      };
    } else {
      return {
        success: false,
        error: 'Resposta inválida do servidor'
      };
    }
  } catch (error) {
    console.error('❌ Token verification error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erro ao verificar token'
    };
  }
};

/**
 * Logout and invalidate JWT token
 */
export const logout = async (): Promise<AuthApiResponse> => {
  try {
    const token = tokenStorage.getToken();
    
    console.log('🚪 Performing logout...');
    
    if (token) {
      try {
        const response = await fetch(`${API_BASE}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          console.log('✅ Server logout successful');
        } else if (response.status === 401) {
          console.log('🔐 Token already invalid on server');
        } else {
          console.warn('⚠️ Server logout failed, but continuing with local cleanup');
        }
      } catch (apiError) {
        console.warn('⚠️ Server logout request failed:', apiError);
      }
    }

    // Always clear local storage regardless of API response
    tokenStorage.clearAll();
    
    console.log('✅ Local logout completed');
    
    return {
      success: true,
      message: 'Logout realizado com sucesso'
    };
  } catch (error) {
    console.error('❌ Logout error:', error);
    // Still clear local storage even if API call fails
    tokenStorage.clearAll();
    
    return {
      success: true, // Return success since local cleanup worked
      message: 'Logout realizado localmente'
    };
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!tokenStorage.getToken();
};

/**
 * Get current user profile from stored info
 */
export const getCurrentUser = (): UserInfo | null => {
  return tokenStorage.getUserInfo();
};

/**
 * Initialize auth - verify stored token on app startup
 */
export const initializeAuth = async (): Promise<UserInfo | null> => {
  try {
    const token = tokenStorage.getToken();
    const storedUserInfo = tokenStorage.getUserInfo();

    // If no token, return null
    if (!token) {
      console.log('🔐 No token found during initialization');
      return null;
    }

    // If we have stored user info but no token, clear everything
    if (!token && storedUserInfo) {
      console.warn('🔐 Found user info without token, clearing storage');
      tokenStorage.clearAll();
      return null;
    }

    console.log('🔍 Verifying stored token...');
    const verifyResponse = await verifyToken();
    
    if (verifyResponse.success && verifyResponse.user) {
      // Update stored user info with fresh data from server
      tokenStorage.setUserInfo(verifyResponse.user);
      console.log('✅ Token verified successfully');
      return verifyResponse.user;
    } else {
      // Token invalid, clear everything
      console.warn('🔐 Token verification failed, clearing storage');
      tokenStorage.clearAll();
      return null;
    }
  } catch (error) {
    console.error('❌ Auth initialization error:', error);
    // Clear storage on any error
    tokenStorage.clearAll();
    return null;
  }
};

/**
 * Get authorization header for API requests
 */
export const getAuthHeader = (): Record<string, string> => {
  const token = tokenStorage.getToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Make authenticated fetch request with automatic token handling
 */
export const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const authHeaders = getAuthHeader();
  
  // Check if we have a token
  if (!tokenStorage.getToken()) {
    throw new Error('Token de autenticação não encontrado. Faça login novamente.');
  }
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...authHeaders
  };

  const mergedOptions: RequestInit = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  };

  const response = await fetch(url, mergedOptions);

  // Handle 401 (Unauthorized) responses
  if (response.status === 401) {
    console.warn('🔐 Token expired or invalid, clearing auth data');
    tokenStorage.clearAll();
    
    // Dispatch a custom event to notify the app about auth failure
    window.dispatchEvent(new CustomEvent('auth-expired'));
    
    throw new Error('Sessão expirada. Por favor, faça login novamente.');
  }

  return response;
};